/**
 * VideoRecorder - Video message recording using MediaRecorder API
 * Inspired by Rocket.Chat's VideoRecorder implementation
 */

class VideoRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.videoChunks = [];
    this.stream = null;
    this.startTime = null;
    this.isRecording = false;
    this.cameraStarted = false;
    this.videoElement = null;
  }

  /**
   * Check if video recording is supported in this browser
   */
  static isSupported() {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.MediaRecorder
    );
  }

  /**
   * Get supported MIME types for video recording
   */
  static getSupportedMimeType() {
    const types = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return null;
  }

  /**
   * Get file extension based on MIME type
   */
  static getFileExtension(mimeType) {
    if (mimeType?.includes('webm')) return 'webm';
    if (mimeType?.includes('mp4')) return 'mp4';
    return 'webm';
  }

  /**
   * Request camera permission
   */
  static async requestPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach(track => track.stop());
      return { granted: true };
    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        return { granted: false, denied: true, error: 'Camera permission denied' };
      }
      return { granted: false, error: error.message };
    }
  }

  /**
   * Start camera preview (without recording)
   * @param {HTMLVideoElement} videoElement - Video element for preview
   */
  async startPreview(videoElement) {
    if (!VideoRecorder.isSupported()) {
      throw new Error('Video recording not supported in this browser');
    }

    try {
      this.videoElement = videoElement;

      // Get camera and microphone stream
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user', // Front camera
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        }
      });

      // Set video element source
      if (this.videoElement) {
        this.videoElement.srcObject = this.stream;
        this.videoElement.muted = true; // Mute preview to avoid feedback
        await this.videoElement.play();
      }

      this.cameraStarted = true;
      return { stream: this.stream };
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }

  /**
   * Start recording (camera must be started first)
   */
  startRecording() {
    if (!this.cameraStarted || !this.stream) {
      throw new Error('Camera not started. Call startPreview() first.');
    }

    if (this.isRecording) {
      throw new Error('Already recording');
    }

    const mimeType = VideoRecorder.getSupportedMimeType();
    if (!mimeType) {
      throw new Error('No supported video format found');
    }

    try {
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        videoBitsPerSecond: 1500000, // 1.5 Mbps
      });

      this.videoChunks = [];
      this.startTime = Date.now();
      this.isRecording = true;

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.videoChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(1000); // Get data every second

      return {
        mimeType,
        startTime: this.startTime,
      };
    } catch (error) {
      this.isRecording = false;
      throw error;
    }
  }

  /**
   * Stop recording and return the video blob
   */
  stopRecording() {
    return new Promise((resolve, reject) => {
      if (!this.isRecording || !this.mediaRecorder) {
        reject(new Error('Not recording'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const mimeType = this.mediaRecorder.mimeType;
        const blob = new Blob(this.videoChunks, { type: mimeType });
        const duration = this.startTime ? Date.now() - this.startTime : 0;

        this.isRecording = false;
        this.startTime = null;
        this.videoChunks = [];
        this.mediaRecorder = null;

        resolve({
          blob,
          mimeType,
          duration,
          extension: VideoRecorder.getFileExtension(mimeType),
        });
      };

      this.mediaRecorder.onerror = (event) => {
        this.isRecording = false;
        reject(event.error);
      };

      try {
        this.mediaRecorder.stop();
      } catch (error) {
        this.isRecording = false;
        reject(error);
      }
    });
  }

  /**
   * Get current recording duration in milliseconds
   */
  getDuration() {
    if (!this.startTime || !this.isRecording) {
      return 0;
    }
    return Date.now() - this.startTime;
  }

  /**
   * Check if camera is started
   */
  isCameraStarted() {
    return this.cameraStarted;
  }

  /**
   * Check if currently recording
   */
  getIsRecording() {
    return this.isRecording;
  }

  /**
   * Stop preview and clean up (without saving)
   */
  cancel() {
    if (this.mediaRecorder && this.isRecording) {
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        // Ignore errors when canceling
      }
    }
    this.cleanup();
  }

  /**
   * Clean up all resources
   */
  cleanup() {
    this.isRecording = false;
    this.cameraStarted = false;
    this.startTime = null;
    this.videoChunks = [];

    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.mediaRecorder) {
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.onerror = null;
      this.mediaRecorder = null;
    }
  }
}

export default VideoRecorder;
